/**
 * Markdown in and out of the rich document editor (RichDocEditor). In: the
 * stored markdown rendered to HTML (GitHub flavour), with raw HTML shown as
 * text and anything outside the editor's own tags taken out. Out: the edited
 * DOM written back as markdown, the subset the editor can produce: headings,
 * paragraphs, bold, italic, strikethrough, code, links, pictures, lists (nested),
 * quotes, code blocks, rules and tables. A save writes this text as the new
 * version; nothing is stored as an operation.
 */
import { Marked } from "marked";

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Raw HTML in the stored text stays visible as words, never becomes markup.
const marked = new Marked({
  gfm: true,
  breaks: false,
  async: false,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
  },
});

const ALLOWED = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6", "STRONG", "B", "EM", "I", "DEL", "S", "CODE", "PRE",
  "A", "IMG", "UL", "OL", "LI", "BLOCKQUOTE", "HR", "BR", "TABLE", "THEAD", "TBODY", "TR", "TH",
  "TD", "INPUT",
]);
const SAFE_URL = /^(?:https?:|mailto:|\/|#)/i;

/** Only the editor's own tags and safe addresses survive. */
function sanitize(root: HTMLElement) {
  for (const el of Array.from(root.querySelectorAll("*"))) {
    if (!ALLOWED.has(el.tagName)) {
      el.replaceWith(...Array.from(el.childNodes));
      continue;
    }
    for (const attr of Array.from(el.attributes)) {
      const keep =
        (el.tagName === "A" && attr.name === "href" && SAFE_URL.test(attr.value)) ||
        (el.tagName === "IMG" && (attr.name === "alt" || (attr.name === "src" && SAFE_URL.test(attr.value)))) ||
        (el.tagName === "INPUT" && (attr.name === "type" || attr.name === "checked" || attr.name === "disabled"));
      if (!keep) el.removeAttribute(attr.name);
    }
  }
}

export function markdownToHtml(markdown: string): string {
  const html = marked.parse(markdown) as string;
  const holder = document.createElement("div");
  holder.innerHTML = html;
  sanitize(holder);
  return holder.innerHTML;
}

/* ------------------------------------------------------------- out */

const BLOCK = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "UL", "OL", "BLOCKQUOTE", "PRE", "TABLE", "HR", "FIGURE"]);

/** Literal characters that would otherwise turn into marks. */
function escapeText(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/([*`~])/g, "\\$1")
    .replace(/(^|[^\w])_|_(?=[^\w]|$)/g, (m) => m.replace("_", "\\_"));
}

/** Marks hug their words: spaces inside the selection move outside them. */
function wrap(inner: string, mark: string): string {
  const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner);
  const [, lead = "", core = "", trail = ""] = match ?? [];
  return core ? `${lead}${mark}${core}${mark}${trail}` : inner;
}

function inline(node: Node, inCell = false): string {
  let out = "";
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out += escapeText((child.textContent ?? "").replace(/[\s\u00a0]+/g, " "));
      continue;
    }
    if (!(child instanceof HTMLElement)) continue;
    switch (child.tagName) {
      case "BR":
        out += inCell ? " " : "  \n";
        break;
      case "STRONG":
      case "B":
        out += wrap(inline(child, inCell), "**");
        break;
      case "EM":
      case "I":
        out += wrap(inline(child, inCell), "*");
        break;
      case "DEL":
      case "S":
        out += wrap(inline(child, inCell), "~~");
        break;
      case "CODE": {
        const code = child.textContent ?? "";
        const fence = code.includes("`") ? "``" : "`";
        out += `${fence}${code}${fence}`;
        break;
      }
      case "A": {
        const href = child.getAttribute("href") ?? "";
        const words = inline(child, inCell);
        out += href ? `[${words}](${href})` : words;
        break;
      }
      case "IMG":
        out += `![${child.getAttribute("alt") ?? ""}](${child.getAttribute("src") ?? ""})`;
        break;
      case "INPUT":
        break;
      default:
        // A block that landed inside a line (a pasted div, a paragraph in a list item).
        out += inline(child, inCell) + (BLOCK.has(child.tagName) && !inCell ? "  \n" : "");
    }
  }
  return out;
}

/** A paragraph that starts like a heading, a quote or a list is kept a paragraph. */
function guardLineStart(text: string): string {
  return /^(#{1,6}\s|>|[-+*]\s|\d+[.)]\s)/.test(text) ? `\\${text}` : text;
}

const clean = (text: string) => text.replace(/[ \t]+$/gm, "").replace(/\s+$/, "").replace(/^ +/, "");

function list(el: HTMLElement, depth: number): string {
  const ordered = el.tagName === "OL";
  const start = Number(el.getAttribute("start")) || 1;
  const items = Array.from(el.children).filter((c): c is HTMLElement => c.tagName === "LI");
  return items
    .map((li, i) => {
      const marker = ordered ? `${start + i}. ` : "- ";
      const pad = " ".repeat(marker.length);
      const task = li.querySelector(":scope > input[type=checkbox]") as HTMLInputElement | null;
      const words: string[] = [];
      const nested: string[] = [];
      let run = document.createElement("span");
      const flush = () => {
        const text = clean(inline(run));
        if (text) words.push(text);
        run = document.createElement("span");
      };
      for (const child of Array.from(li.childNodes)) {
        if (child instanceof HTMLElement && (child.tagName === "UL" || child.tagName === "OL")) {
          flush();
          nested.push(list(child, depth + 1));
        } else if (child instanceof HTMLElement && child.tagName === "P") {
          flush();
          const text = clean(inline(child));
          if (text) words.push(text);
        } else {
          run.appendChild(child.cloneNode(true));
        }
      }
      flush();
      const box = task ? (task.checked ? "[x] " : "[ ] ") : "";
      const body = words.join(`\n${pad}`);
      const line = `${marker}${box}${body}`;
      const children = nested.map((block) => block.replace(/^/gm, pad)).join("\n");
      return children ? `${line}\n${children}` : line;
    })
    .join("\n");
}

function table(el: HTMLElement): string {
  const rows = Array.from(el.querySelectorAll("tr"));
  if (rows.length === 0) return "";
  const cells = rows.map((row) =>
    Array.from(row.children).map((cell) => clean(inline(cell, true)).replace(/\|/g, "\\|") || " "),
  );
  const width = Math.max(...cells.map((row) => row.length));
  const line = (row: string[]) =>
    `| ${Array.from({ length: width }, (_, i) => row[i] ?? " ").join(" | ")} |`;
  const [head, ...body] = cells;
  return [line(head), `| ${Array.from({ length: width }, () => "---").join(" | ")} |`, ...body.map(line)].join("\n");
}

function blocks(root: Node): string[] {
  const out: string[] = [];
  let loose = document.createElement("span");
  const flushLoose = () => {
    const text = clean(inline(loose));
    if (text) out.push(guardLineStart(text));
    loose = document.createElement("span");
  };
  for (const child of Array.from(root.childNodes)) {
    if (!(child instanceof HTMLElement) || !BLOCK.has(child.tagName)) {
      // Text typed straight into the editor, or a lone inline mark.
      if (child instanceof HTMLElement && child.tagName === "BR") {
        flushLoose();
        continue;
      }
      loose.appendChild(child.cloneNode(true));
      continue;
    }
    flushLoose();
    const tag = child.tagName;
    if (tag === "P" || tag === "DIV") {
      if (Array.from(child.children).some((c) => BLOCK.has(c.tagName))) {
        out.push(...blocks(child));
      } else {
        const text = clean(inline(child));
        if (text) out.push(guardLineStart(text));
      }
    } else if (/^H[1-6]$/.test(tag)) {
      const text = clean(inline(child));
      if (text) out.push(`${"#".repeat(Number(tag[1]))} ${text}`);
    } else if (tag === "UL" || tag === "OL") {
      const text = list(child, 0);
      if (text.trim()) out.push(text);
    } else if (tag === "BLOCKQUOTE") {
      const inner = blocks(child).join("\n\n");
      if (inner) out.push(inner.replace(/^/gm, "> "));
    } else if (tag === "PRE") {
      const code = (child.textContent ?? "").replace(/\n$/, "");
      out.push(`\`\`\`\n${code}\n\`\`\``);
    } else if (tag === "TABLE") {
      const text = table(child);
      if (text) out.push(text);
    } else if (tag === "HR") {
      out.push("---");
    } else if (tag === "FIGURE") {
      out.push(...blocks(child));
    }
  }
  flushLoose();
  return out;
}

export function htmlToMarkdown(root: HTMLElement): string {
  return blocks(root).join("\n\n") + "\n";
}
